import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { AjaxListingContext, AjaxListingPagination } from '$partials/base/ajax-listing.jsx';
import { ArticleStandardListingItem } from '$partials/listings/items';
import { Footer, HeaderLandingClosed, HeaderLandingOpen, Meta } from '$partials/global';
import { ModuleLargeStatement, ModuleRelatedCarousel, ModuleSecondaryHero} from '$partials/modules/individual/index.js';
import { useAjaxListing } from '$hooks/ajax-listing';
import { useLocale } from '$contexts/locale.jsx';
import Button from '$partials/ui/button';
import Image from '$partials/media/image';
import { getBgColor } from '$utilities/data';
import { IconChevronSprite } from '$partials/icons/index.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

const Marker = ({ colour = '#FC5200' }) => {
    return (
        <svg
            className='inline'
            style={{ width: '8px', height: '8px' }}
            viewBox='0 0 8 8'
            xmlns='http://www.w3.org/2000/svg'>
            <circle
                cx='4'
                cy='4'
                fill={colour}
                r='4' />
        </svg>
    );
};

const FilterList = ({
    appliedFilter,
    filterOption,
    index,
    setFilter
}) => {
    let isCurrent = appliedFilter.title === filterOption.title;

    return (
        <div className='text-center'>
            <button
                className='type-style-7'
                onClick={() => setFilter(filterOption)}
                type='button'>
                {filterOption.title}
            </button>
            {isCurrent &&
                <div className={`z-50 absolute left-0 right-0 mt-5 ${index === 0 ? 'pr-20' : ''}`}>
                    <Marker />
                </div>
            }

            {!isCurrent &&
                <div className={`z-50 absolute left-0 right-0 mt-5 invisible group-hover:visible ${index === 0 ? 'pr-20' : ''}`}>
                    <Marker colour='#00000' />
                </div>
            }
        </div>
    );
};

/**
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const CaseStudiesListingPage = ({ data, domain }) => {
    const page = data?.page;

    const [isOpen, setIsOpen] = useState(false);

    const { current: locale, translate: t } = useLocale();
    const [filter, setFilter] = useState(data.categories?.find(category => category.slug === data?.items?.filters?.category));

    let query = { locale: locale?.code, category: filter?.slug };
    const listing = useAjaxListing({
        items: data?.items?.items,
        page: data?.items?.page || 1,
        path: '/api/v1/listings/case-studies',
        totalItems: data?.items?.total,
        totalPages: data?.items?.totalPages,
        query
    });

    return (
        <>
            <Meta page={page} />

            <main
                className='min-h-screen relative'
                id='main-content'>

                <HeaderLandingOpen
                    bgStyle={isOpen ? 'white' : 'transparent'}
                    isOpen={isOpen}
                    menu={data?.navigation}
                    setIsOpen={setIsOpen} />

                {/* Hero */}
                {page?.heroMedia && (
                    <div className='relative sm:sticky sm:top-0'>
                        <figure className='max-w-full'>
                            <div className='relative aspect-[375/425] sm:aspect-[2.35/1]'>
                                <div className='h-full left-1/2 -translate-x-1/2 relative w-full'>
                                    {page?.heroMedia?.contentType?.includes('video/') ?
                                        (
                                            <video
                                                autoPlay
                                                loop
                                                muted
                                                playsInline
                                                className='w-full h-full object-cover'
                                                src={page?.heroMedia?.src}
                                                width={'100%'}/>
                                        ) : (
                                            <Image
                                                {...page?.heroMedia}
                                                className='w-full h-full object-cover'
                                                containerClassName='w-full h-full relative'
                                                height='100%'
                                                loadWidth={2560}
                                                width='100%' />
                                        )
                                    }
                                </div>
                                <div
                                    className='absolute w-full h-full top-0'
                                    style={{
                                        backgroundColor: getBgColor(page?.heroOverlayColor) || '#FC5200',
                                        opacity: `${(page?.heroOverlayOpacity || '45')}%`}} />
                            </div>
                        </figure>
                        <div className='absolute top-0 left-0 z-50 w-full'>
                            <HeaderLandingClosed
                                bgStyle={isOpen ? 'white' : 'transparent'}
                                isOpen={isOpen}
                                menu={data?.navigation}
                                setIsOpen={setIsOpen} />
                        </div>
                        <div className='absolute bottom-0 left-0 z-10 w-full mb-40 md:mb-60'>
                            <div className='layout-container flex flex-col'>
                                <p className='px-10 sm:px-5 md:px-10 w-full sm:w-7/12 type-style-2 md:type-style-1 text-white'>
                                    {page.heroTitle}
                                </p>
                                {page.heroCtaLabel && page.heroCtaLink && (
                                    <div className='px-10 flex self-start mt-20 md:mt-30'>
                                        <Button
                                            as='a'
                                            className='button button--primary'
                                            href={page.heroCtaLink}>
                                            <span>
                                                {page.heroCtaLabel}
                                            </span>
                                            <span className='button__icon button__icon--animate'>
                                                <IconChevronSprite />
                                            </span>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}


                {/* Related Items */}
                {page.relatedItems?.items?.length > 0 && (
                    <div className='relative bg-white transform-gpu'>
                        <ModuleRelatedCarousel
                            items={page.relatedItems.items}
                            parent={'Listing'}
                            title={page.relatedTitle}/>
                    </div>
                )}

                <div className='pt-20 relative bg-white transform-gpu'>
                    <article>

                        <ModuleLargeStatement
                            eyebrowTitle={page?.largeStatementEyebrow}
                            title={page?.largeStatementTitle} />

                        {/* Secondary Hero */}
                        {/* {page.secondaryHero && (
                            <ModuleSecondaryHero
                                ctaLabel={page.secondaryHero.ctaLabel}
                                ctaLink={page.secondaryHero.ctaLink}
                                eyebrowIcon={page.secondaryHero.eyebrowIcon}
                                eyebrowTitle={page.secondaryHero.eyebrowTitle}
                                image={page.secondaryHero.image}
                                imageMd={page.secondaryHero.imageMd}
                                parent={'Listing'}
                                title={page.secondaryHero.title}/>
                        )} */}
                        <div className='relative bg-white'>

                            <div className='layout-container'>

                                {/* Header */}
                                <header className='px-10 sm:px-5 md:px-10 pt-60 md:pt-80'>
                                    <h1 className='type-style-3 w-full sm:w-4/6 md:w-10/12'>
                                        {t('All case studies')}
                                    </h1>
                                    {data.categories?.length > 1 && page.displayFilters && (
                                        <div className='type-style-7 pt-30 md:pt-50'>
                                            {data.categories.length > 1 && (
                                                <div className='flex flex-col w-screen sm:w-auto -ml-20 pl-20'>
                                                    <ul className='list-none flex overflow-x-scroll pb-20 categories-list'>
                                                        {data.categories.map((item, idx) => (
                                                            <li
                                                                className={`group inline-block relative flex-shrink-0 ${!idx ? 'pr-20' : 'px-20'} pb-15`}
                                                                key={item.slug}>
                                                                <FilterList
                                                                    appliedFilter={filter}
                                                                    filterOption={item}
                                                                    index={idx}
                                                                    setFilter={setFilter} />
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <hr className='w-full border border-light-grey/20 -mt-20'/>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </header>
                            </div>
                        </div>

                        <div className='bg-white relative'>

                            <div className='layout-container'>

                                {/* Listing */}
                                <AjaxListingContext.Provider value={listing}>
                                    <section
                                        aria-busy={listing.loading}
                                        aria-label='Articles'
                                        className='flex flex-wrap pt-60 md:pt-80'
                                        role='feed'>
                                        {listing?.items?.length > 0 ? (listing?.items?.map((item, i) => {
                                            if (!item) { return null; }
                                            return (
                                                <article
                                                    aria-label={item.title}
                                                    aria-posinset={i + 1}
                                                    aria-setsize={listing.totalItems}
                                                    className={'flex flex-col w-full sm:w-6/12 md:w-4/12 pb-50 md:pb-70 px-10 sm:px-5 md:px-10'}
                                                    key={`${item.sys?.id}-${i}`}
                                                    tabIndex={-1}>
                                                    <ArticleStandardListingItem
                                                        {...item}
                                                        tags={item.categories}
                                                        url={`/case-studies/${item.slug}`}/>
                                                </article>
                                            );
                                        })) : (
                                            <div className='px-10 sm:px-5 md:px-10 mb-60'>No results</div>
                                        )}

                                        {listing.totalPages > 1 && <div className='border-b border-dark-grey mx-10 sm:mx-5 md:px-10 w-full' />}
                                    </section>
                                    {listing.totalPages > 1 && (
                                        <div className='w-full px-10 sm:px-5 md:mx-10 mt-30 mb-20 sm:mb-0 flex items-start'>
                                            <AjaxListingPagination
                                                current={listing.page}
                                                totalPages={listing.totalPages}/>
                                        </div>
                                    )}
                                </AjaxListingContext.Provider>
                            </div>
                        </div>
                    </article>

                    <div className='bg-white relative'>
                        <div className='layout-container pt-40 sm:pt-50 md:pt-70'>
                            <Footer
                                domain={domain}
                                hideBorder={listing.totalPages > 1}
                                menu={data?.footer} />
                        </div>
                    </div>
                </div>

            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Marker.propTypes = {
    colour: PropTypes.string,
};

FilterList.propTypes = {
    appliedFilter: PropTypes.object,
    filterOption: PropTypes.object,
    index: PropTypes.number,
    setFilter: PropTypes.func,
};

const propTypes = {
    data: PropTypes.shape({
        categories: PropTypes.array,
        items: PropTypes.shape({
            filters: PropTypes.object,
            items: PropTypes.array,
            page: PropTypes.number,
            total: PropTypes.number,
            totalPages: PropTypes.number,
        }),
        page: PropTypes.object,
        footer: PropTypes.shape({
            items: PropTypes.array,
        }),
        search: PropTypes.object,
        navigation: PropTypes.object,
    }),
    domain: PropTypes.string,
};

CaseStudiesListingPage.propTypes = propTypes;

export default CaseStudiesListingPage;
